import Component from '@glimmer/component'
import type { RexDashboardTableRoute } from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/table/route'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { tracked } from '@glimmer/tracking'
import { t } from 'ember-intl'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import isSubco from 'teacher-ui-client/helpers/is-subco'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { array } from '@ember/helper'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/helpers/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import RouteTemplate from 'ember-route-template'
import type { PhosphorIconName } from 'phosphor-icons'

interface Signature {
  Args: { model: ModelFor<RexDashboardTableRoute> }
}

export class RexDashboardTablePage extends Component<Signature> {
  @service router!: RouterService

  // These are set by statefulSort action
  @tracked currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'
  @tracked currentSortColumn?: string

  readonly routePrefix = 'reporting.ui-scope.rex.dashboard'

  get studentColspan() {
    return isSubco ? 5 : 3
  }

  get isSummaryDataPresent() {
    return isPresent(this.args.model?.summary)
  }

  get summaryRow() {
    const totals = this.args.model?.summary?.school

    return {
      assessmentsCompleted: totals?.assessments_completed ?? null,
      booksRead: totals?.books_read ?? null,
      currentLessonLexile: totals?.average_current_lesson_lexile ?? null,
      initialLessonLexile: totals?.average_initial_lesson_lexile ?? null,
      lessonsCompleted: totals?.lessons_completed ?? null,
      lessonQuizzesAttempted: totals?.lesson_quizzes_attempted ?? null,
      lessonQuizzesAverageScorePercent: totals?.lesson_quizzes_average_score_percent ?? null,
      lexileGrowth: totals?.average_lexile_growth ?? null,
      stadiumEvents: totals?.stadium_events ?? null,
      studentCount: totals?.student_count ?? null,
      spellingCompleted: totals?.spelling_completed ?? null,
      totalTime: totals?.total_time ?? null,
      usageScore: totals?.average_usage_score ?? null,
    }
  }

  get rows() {
    const statsData = this.args.model?.summary?.students

    return this.args.model.scopedStudents.map((student) => {
      const foundStatsDataForStudent = statsData?.find((item) => String(item?.student_id) === student.id) ?? {}
      return {
        studentId: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        studentExternalId: student.externalId,
        gradePosition: student.gradePosition,
        ...foundStatsDataForStudent,
        teacherName: student
          .teachersFor(SubscriptionType.Reading)
          .map((teacher) => teacher.fullName)
          .join(', '),
        schoolClasses: student
          .schoolClassesFor(SubscriptionType.Reading)
          .map((schoolClass) => schoolClass.name)
          .join(', '),
      }
    })
  }

  statefulSort = (tableAction: TableAction, tableActionOperation: string, tableActionKey: string) => {
    statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.rex.dashboard.student', studentId)
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.rex"}} />
    <ReportingDashboardControls
      @students={{@model.scopedStudents}}
      @routePrefix={{this.routePrefix}}
      @exportOptions={{array ExportType.CSV}}
      @isDataPresent={{this.isSummaryDataPresent}}
      @studentRouteName="reporting.ui-scope.rex.dashboard.student"
    />
    {{#if this.isSummaryDataPresent}}
      <ContentTile>
        <div class="max-h-192 overflow-auto print:max-h-[unset] print:overflow-visible">
          <DataTableOperations @data={{this.rows}} as |transformedData tableAction|>
            <table data-test-dashboard-table class="table-print-zoom-1/2 print:divide-y print:divide-black" exportable>
              <thead class="sticky top-0 font-semibold print:divide-y print:divide-black">
                {{! Using box shadow, we can get a border between the header rows, even with the sticky header }}
                <tr
                  class="bg-oceany-blue-150 divide-x divide-white text-center text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <th class="p-3" colspan={{this.studentColspan}}>
                    {{t "reporting.dashboardTables.students"}}
                  </th>
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <th class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.lessonsCompleted.rex"}}
                  </th>
                  <th class="p-3" colspan="2">
                    {{t "reporting.dashboardTables.quizzesCompleted.rex"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.initialLexile"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.highestLexile"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.lexileGrowth"}}
                  </th>
                  <th class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.assessmentsCompleted"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.booksRead"}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.spellingLessonsCompleted" htmlSafe=true}}
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.stadium"}}
                  </th>
                  <th class="p-3 whitespace-nowrap">
                    {{t "reporting.dashboardTables.usage"}}
                    <span class="print:hidden">
                      <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
                      <Tooltip @text={{t "reporting.dashboardTables.usageTooltip.rex"}} role="presentation" />
                    </span>
                  </th>
                  <th class="p-3">
                    {{t "reporting.dashboardTables.timeInProgram"}}
                  </th>
                </tr>
                <tr
                  class="bg-oceany-blue-100 divide-x divide-white text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "firstName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "firstName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "lastName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lastName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th class="hidden">
                    {{t "reporting.dashboardTables.studentExternalId"}}
                  </th>
                  <th
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "schoolClasses")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.classes"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "schoolClasses")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  {{#if isSubco}}
                    <th
                      class="min-w-40 p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "teacherName")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "reporting.dashboardTables.teacher"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "teacherName")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </th>
                    <th
                      class="p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "gradePosition")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "grade.label"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "gradePosition")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </th>
                  {{/if}}
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.initialLesson.rex" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "initial_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "current_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestLessonCompleted.rex" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "current_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="relative p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lessons_completed")}}
                    role="button"
                  >
                    <div class="flex items-center gap-1">
                      {{t "reporting.dashboardTables.totalCompleted"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lessons_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="ml-auto shrink-0 print:hidden"
                        role="presentation"
                      />
                      {{#if isSubco}}
                        <span class="print:hidden">
                          <PhosphorIcon
                            @icon="phosphor-info-fill"
                            class="text-dusty-black-500 cursor-pointer"
                            role="presentation"
                          />
                          <Tooltip @text={{t "reporting.dashboardToolTipTotals"}} role="presentation" />
                        </span>
                      {{/if}}
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lesson_quizzes_attempted")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.numberOfAttempts" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lesson_quizzes_attempted")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on
                      "click"
                      (fn this.statefulSort tableAction "sortByNumber" "lesson_quizzes_average_score_percent")
                    }}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.averageLessonQuizScore.rex" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lesson_quizzes_average_score_percent")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_lexile")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.initialLexile"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "initial_lesson_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "current_lesson_lexile")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestLexile"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "current_lesson_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lexile_growth")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.lexileGrowth"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lexile_growth")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "assessments_completed")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalCompleted"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "assessments_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on
                      "click"
                      (fn this.statefulSort tableAction "sortByNumber" "assessments_complete_fiction_last_lexile")
                    }}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestFiction" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "assessments_complete_fiction_last_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on
                      "click"
                      (fn this.statefulSort tableAction "sortByNumber" "assessments_complete_nonfiction_last_lexile")
                    }}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestNonfiction" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "assessments_complete_nonfiction_last_lexile")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "books_read")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.booksRead"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "books_read")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "spelling_completed")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.spellingLessonsCompleted" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "spelling_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "stadium_events")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.stadium"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "stadium_events")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "usage_score")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.usage"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "usage_score")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                  <th
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "total_time")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalHours"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "total_time")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </th>
                </tr>
                <tr class="bg-oceany-blue-100 text-black shadow-[inset_0_-1px_0] shadow-white" data-test-summary-row>
                  <td class="p-3" colspan={{this.studentColspan}}>
                    {{t "reporting.dashboardTables.totals"}}
                  </td>
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <td class="p-3">
                    -
                  </td>
                  <td class="p-3">
                    -
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.lessonsCompleted}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.lessonQuizzesAttempted}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.lessonQuizzesAverageScorePercent append="%"}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.initialLessonLexile}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.currentLessonLexile}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.lexileGrowth}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.assessmentsCompleted}}
                  </td>
                  <td class="p-3" colspan="2">
                    -
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.booksRead}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.spellingCompleted}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.stadiumEvents}}
                  </td>
                  <td class="p-3">
                    {{formatValue this.summaryRow.usageScore}}
                  </td>
                  <td class="p-3">
                    {{formatDuration this.summaryRow.totalTime "-"}}
                  </td>
                </tr>
              </thead>
              <tbody class="print:divide-dusty-black-100 print:divide-y">
                {{#each transformedData as |row index|}}
                  {{! template-lint-disable no-invalid-interactive }}
                  <tr
                    data-test-table-row={{index}}
                    class="even:bg-oceany-blue-50 hover:bg-oceany-blue-100 cursor-pointer bg-white"
                    {{on "click" (fn this.transitionToStudentRoute row.studentId)}}
                  >
                    <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere">
                      {{row.firstName}}
                    </td>
                    <td class="text-oceany-blue-300 p-3 font-bold wrap-anywhere">
                      {{row.lastName}}
                    </td>
                    <td class="hidden">
                      {{row.studentExternalId}}
                    </td>
                    <td class="p-3">
                      {{row.schoolClasses}}
                    </td>
                    {{#if isSubco}}
                      <td class="p-3">
                        {{row.teacherName}}
                      </td>
                      <td class="p-3">
                        {{gradeName row.gradePosition}}
                      </td>
                    {{/if}}
                    <td class="p-3">
                      {{formatValue row.initial_lesson_number}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.current_lesson_number}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lessons_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lesson_quizzes_attempted}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lesson_quizzes_average_score_percent append="%"}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.initial_lesson_lexile}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.current_lesson_lexile}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lexile_growth}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.assessments_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.assessments_complete_fiction_last_lexile append="L"}}
                      {{formatValue row.assessments_complete_fiction_last_percentage append="%" replacement=""}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.assessments_complete_nonfiction_last_lexile append="L"}}
                      {{formatValue row.assessments_complete_nonfiction_last_percentage append="%" replacement=""}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.books_read}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.spelling_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.stadium_events}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.usage_score}}
                    </td>
                    <td class="p-3">
                      {{formatDuration row.total_time "-"}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </DataTableOperations>
        </div>
      </ContentTile>
    {{else}}
      <NoDataMessage />
    {{/if}}
  </template>
}

export default RouteTemplate(RexDashboardTablePage)
